import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  // #onQuantityChange(event) {
  //   if (!(event.target instanceof Node) || !this.contains(event.target)) return;

  //   const { quantity, cartLine: line } = event.detail;

  //   // Cart items require a line number
  //   if (!line) return;

  //   if (quantity === 0) {
  //     return this.onLineItemRemove(line);
  //   }

  //   this.updateQuantity({
  //     line,
  //     quantity,
  //     action: 'change',
  //   });
  //   const lineItemRow = this.refs.cartItemRows[line - 1];

  //   if (!lineItemRow) return;

  //   const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
  //   textComponent?.shimmer();
  // }

   #onQuantityChange(event) {
  if (!(event.target instanceof Node) || !this.contains(event.target)) return;

  const { quantity: rawQuantity, cartLine: line } = event.detail;

  if (!line) return;

  // Safeguard: never allow a negative quantity to propagate through the update pipeline.
  // Math.max clamps to 0 (which triggers line removal on the backend) while preventing
  // the UI from ever rendering a negative intermediate value.
  const quantity = Math.max(0, Number(rawQuantity) || 0);

  const row = this.refs.cartItemRows[line - 1];
  if (!row) return;

  // ===== ADDON PER-VARIANT AGGREGATED CAP CHECK =====
  // Cap is enforced per (parent main, variant) GROUP, summed across every
  // line item of that variant. A 3-knife bundle (main qty=1, bundle=3) allows
  // total variant-X quantity = 3 across however many line items, NOT 3 per
  // line. Different variants remain independent of one another.
  if (row.hasAttribute('data-addon-product')) {
    const addonInput = row.querySelector('input');

    // defaultValue reflects the last server-synced cart quantity for this
    // line (re-baked on every morphSection render). Used as an absolute
    // floor so a "+" click past the cap cannot regress the line to 0
    // (which would otherwise remove it from the cart).
    const previousValue = Math.max(
      0,
      parseInt(addonInput?.defaultValue ?? '', 10) || 0
    );

    const mainRow = this.#findMainRowForAddon(row);
    if (mainRow) {
      const mainQty = Math.max(0, Number(mainRow.querySelector('input')?.value || 0));
      const bundleItemCount = Math.max(
        1,
        parseInt(mainRow.dataset.bundleItemCount ?? '', 10) || 1
      );
      const variantCap = mainQty * bundleItemCount;

      // Aggregate the SAME variant across every linked line item under the
      // same parent main. Sibling lines of OTHER variants don't count.
      const sameVariantSiblings = this.#findSameVariantAddonRows(row, mainRow);
      const siblingsTotal = sameVariantSiblings.reduce((sum, sib) => {
        const v = Math.max(0, Number(sib.querySelector('input')?.value || 0));
        return sum + v;
      }, 0);

      // The input was already updated to `quantity` for THIS row before the
      // event fired, so we exclude it and re-add `quantity` to compute the
      // aggregated total being requested.
      const currentRowValue = Math.max(0, Number(addonInput?.value || 0));
      const aggregatedTotal = siblingsTotal - currentRowValue + quantity;

      if (aggregatedTotal > variantCap) {
        // Cap THIS row down so the aggregated total fits, never shrink it
        // below previousValue (so "+" past the cap leaves the row untouched).
        const remainingBudget = Math.max(0, variantCap - (siblingsTotal - currentRowValue));
        const allowedQty = Math.max(previousValue, Math.min(quantity, remainingBudget));

        if (addonInput) addonInput.value = String(allowedQty);

        if (allowedQty === previousValue) {
          // No effective change — don't hit the cart API.
          return;
        }

        return this.updateQuantity({
          line,
          quantity: allowedQty,
          action: 'change',
        });
      }
    }
  }
  // ===== END ADDON CHECK =====

  // ===== MAIN PRODUCT CHANGE CHECK =====
  if (row.hasAttribute('data-main-product')) {
    // Quantity 0 on a main = full removal. Cascade-remove all linked addons
    // atomically so the user can never check out with orphaned addons.
    if (quantity === 0) {
      return this.#removeMainAndAddons(row, line);
    }

    // For bundle products the per-variant cap scales with bundleItemCount
    // (e.g. a 3-knife bundle at qty=2 allows each variant total up to 6).
    const bundleItemCount = Math.max(
      1,
      parseInt(row.dataset.bundleItemCount ?? '', 10) || 1
    );
    const variantCap = quantity * bundleItemCount;

    // Group linked addon rows by variant ID and collect every shrink the new
    // main qty forces. Build a SINGLE /cart/update.js payload keyed by
    // item.key so the main update + every addon shrink land atomically.
    // Without this, multiple parallel /cart/change.js calls would race:
    // once the first response morphs the section, line indices shift and
    // the remaining in-flight calls hit the wrong lines — that's the bug
    // the user saw where addon quantities weren't trimmed at checkout.
    /** @type {Record<string, number>} */
    const updates = {};
    const mainKey = row.dataset.key;
    let anyShrink = false;

    const groups = this.#groupAddonRowsByVariant(row);
    groups.forEach((groupRows) => {
      const total = groupRows.reduce(
        (sum, gr) => sum + Math.max(0, Number(gr.querySelector('input')?.value || 0)),
        0
      );
      if (total <= variantCap) return; // already within cap

      // Trim from the END so earlier line items (often the ones the user
      // explicitly configured first) are preserved when possible.
      let remaining = variantCap;
      groupRows.forEach((groupRow) => {
        const input = groupRow.querySelector('input');
        if (!input) return;
        const currentValue = Math.max(0, Number(input.value) || 0);
        const newQty = Math.max(0, Math.min(currentValue, remaining));
        remaining = Math.max(0, remaining - newQty);

        if (newQty !== currentValue) {
          // Reflect the clamped value in the UI now so the user never sees
          // the over-cap quantity between dispatch and server response.
          input.value = String(newQty);
          const key = groupRow.dataset.key;
          if (key) {
            updates[key] = newQty;
            anyShrink = true;
          }
        }
      });
    });

    // If any shrinks were needed, batch main + all shrinks into one atomic
    // request and skip the fall-through single-line update for the main.
    if (anyShrink && mainKey) {
      updates[mainKey] = quantity;
      return this.#batchUpdateByKey(updates);
    }
    // No shrinks needed → fall through to the regular single-line update
    // for the main only (keeps shimmer + morph semantics intact).
  }

  // ===== NORMAL CART UPDATE =====
  if (quantity === 0) {
    return this.onLineItemRemove(line);
  }

  this.updateQuantity({
    line,
    quantity,
    action: 'change',
  });

  const lineItemRow = this.refs.cartItemRows[line - 1];

  if (!lineItemRow) return;

  const textComponent = lineItemRow.querySelector('text-component');
  textComponent?.shimmer();
}

  /**
   * Returns the main-product row that owns the given addon row.
   * Matches by data-parent-product-title === main's data-product-title.
   * Falls back to the first main in the cart for legacy carts that pre-date
   * the parent-title data attributes (single-main carts).
   *
   * @param {HTMLElement} addonRow
   * @returns {HTMLElement | null}
   */
  #findMainRowForAddon(addonRow) {
    const parentTitle = addonRow.dataset.parentProductTitle;
    if (parentTitle) {
      const mainRows = this.querySelectorAll('[data-main-product="true"]');
      for (const mainRow of mainRows) {
        if ((mainRow.dataset.productTitle || '') === parentTitle) {
          return /** @type {HTMLElement} */ (mainRow);
        }
      }
    }
    return /** @type {HTMLElement | null} */ (
      this.querySelector('[data-main-product="true"]')
    );
  }

  /**
   * Returns all addon rows that belong to the given main row, matched by
   * data-parent-product-title === main's data-product-title. Falls back to
   * every addon row if the main has no title (legacy single-main carts).
   *
   * @param {HTMLElement} mainRow
   * @returns {HTMLElement[]}
   */
  #findAddonRowsForMain(mainRow) {
    const mainTitle = mainRow.dataset.productTitle || '';
    const allAddons = Array.from(
      this.querySelectorAll('[data-addon-product="true"]')
    );
    if (!mainTitle) return /** @type {HTMLElement[]} */ (allAddons);
    return /** @type {HTMLElement[]} */ (
      allAddons.filter(
        (row) => (row.dataset.parentProductTitle || '') === mainTitle
      )
    );
  }

  /**
   * Returns every addon row under `mainRow` that shares the same variant id
   * as `addonRow`, INCLUDING `addonRow` itself. Used to aggregate quantity
   * across multiple line items of the same variant (e.g. multiple engraving
   * lines of the same variant) so the cap applies to the total, not per row.
   *
   * @param {HTMLElement} addonRow
   * @param {HTMLElement} mainRow
   * @returns {HTMLElement[]}
   */
  #findSameVariantAddonRows(addonRow, mainRow) {
    const variantId = addonRow.dataset.addonVariantId || '';
    if (!variantId) return [addonRow];
    return this.#findAddonRowsForMain(mainRow).filter(
      (row) => (row.dataset.addonVariantId || '') === variantId
    );
  }

  /**
   * Groups every addon row under `mainRow` by variant id. Returns an array
   * of row arrays — each inner array contains all line items of one variant.
   * Rows lacking a variant id are placed in their own singleton group so
   * the caller's cap logic still treats them.
   *
   * @param {HTMLElement} mainRow
   * @returns {HTMLElement[][]}
   */
  #groupAddonRowsByVariant(mainRow) {
    /** @type {Map<string, HTMLElement[]>} */
    const map = new Map();
    /** @type {HTMLElement[][]} */
    const ungroupedSingletons = [];
    for (const row of this.#findAddonRowsForMain(mainRow)) {
      const variantId = row.dataset.addonVariantId || '';
      if (!variantId) {
        ungroupedSingletons.push([row]);
        continue;
      }
      const existing = map.get(variantId);
      if (existing) {
        existing.push(row);
      } else {
        map.set(variantId, [row]);
      }
    }
    return [...map.values(), ...ungroupedSingletons];
  }

  /**
   * Atomically remove a main product line + every addon line linked to it.
   * Delegates to #batchUpdateByKey which posts a single /cart/update.js
   * request so removal can never leave addons orphaned.
   *
   * @param {HTMLElement} mainRow
   * @param {number} mainLine
   */
  #removeMainAndAddons(mainRow, mainLine) {
    const addonRows = this.#findAddonRowsForMain(mainRow);
    const mainKey = mainRow.dataset.key;

    // No keys / no addons → fall back to the existing single-line removal,
    // which preserves all prior behaviour for non-bundle / non-addon carts.
    // Use #singleLineRemove (not onLineItemRemove) to avoid re-entering the
    // cascade detection branch, which would otherwise recurse forever.
    if (!mainKey || addonRows.length === 0) {
      return this.#singleLineRemove(mainLine);
    }

    /** @type {Record<string, number>} */
    const updates = { [mainKey]: 0 };
    for (const addonRow of addonRows) {
      const key = addonRow.dataset.key;
      if (key) updates[key] = 0;
    }

    this.#batchUpdateByKey(updates, () => this.#singleLineRemove(mainLine));
  }

  /**
   * Post a single atomic /cart/update.js request for an arbitrary set of
   * line updates keyed by item.key. This is the correct primitive whenever
   * multiple cart lines must be updated together — calling /cart/change.js
   * per line races because indices shift as each response lands and morphs
   * the section, so in-flight calls hit the wrong lines.
   *
   * @param {Record<string, number>} updates - Map of item.key → new quantity
   * @param {() => void} [onError] - Optional fallback if the server rejects
   *   the batch or the network fails.
   */
  #batchUpdateByKey(updates, onError) {
    this.#disableCartItems();

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    this.refs.cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsed = JSON.parse(responseText);

        resetShimmer(this);

        if (parsed.errors) {
          if (onError) onError();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsed.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML
          .querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.#updateQuantitySelectors(parsed);

        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsed.sections,
          })
        );

        morphSection(this.sectionId, parsed.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error('[CartItems] batch cart update failed:', error);
        if (onError) onError();
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    // If the user removes a MAIN bundle product via the trash icon, all of
    // its linked addons must be removed too — otherwise the customer can
    // check out with orphaned addons (Problem 1). #removeMainAndAddons does
    // this atomically via /cart/update.js.
    if (
      cartItemRowToRemove instanceof HTMLElement &&
      cartItemRowToRemove.hasAttribute('data-main-product') &&
      this.#findAddonRowsForMain(cartItemRowToRemove).length > 0
    ) {
      return this.#removeMainAndAddons(cartItemRowToRemove, line);
    }

    this.#singleLineRemove(line);
  }

  /**
   * Original single-line removal flow, factored out so the cascade-removal
   * fallback path can reuse it without re-entering onLineItemRemove (which
   * would otherwise recurse if the main has linked addons).
   *
   * @param {number} line - The line item index.
   */
  #singleLineRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    // Clamp defaultValue to a non-negative integer so a bad server state can
    // never cause the UI to render a negative quantity on revert.
    const revertValue = Math.max(0, parseInt(quantityInput.defaultValue, 10) || 0);
    quantityInput.value = String(revertValue);

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
